<script>
  import { onMount } from "svelte";
  import {
    Page,
    Row,
    Col,
    Button,
    Card,
    CardContent,
    Preloader,
    f7,
    f7ready,
  } from "framework7-svelte";

  import AdminNavbar from "../components/common/AdminNavbar.svelte";
  import KpiStatCard from "../components/common/KpiStatCard.svelte";
  import StatusBadge from "../components/common/StatusBadge.svelte";
  import CsvExportButton from "../components/common/CsvExportButton.svelte";

  import { adminQuery, orgMutation } from "../services/adminGQL";
  import { sanitizeRecords } from "../utils/dataSanitizer";
  import { formatINR, formatPercent, formatNumber } from "../utils/formatters";

  export let f7router;
  export let f7route;

  let rawUsers = [];
  let loading = false;
  let activeFilter = "ALL"; // 'ALL' | 'ANOMALIES' | 'CRITICAL' | 'WARNING' | 'HEALTHY'
  let searchQuery = "";
  let ledgerThreshold = 0;
  let impersonatingId = null;

  onMount(() => {
    f7ready(() => {
      fetchAllHomeData();
    });
  });

  async function fetchAllHomeData() {
    loading = true;
    try {
      const res = await adminQuery(
        "get_all_users_homepage_data",
        {},
        "all_home_data { user_name user_id mobile ucc investment_amount payout_amount profit_loss_amount performance_percentage portfolio_value ledger_balance dp_holding_value ledger_balance_type dp_total_value previous_longshort_value longshort_value account_opening_date first_transaction_date }"
      );

      const list = res?.data?.get_all_users_homepage_data?.all_home_data || [];
      rawUsers = sanitizeRecords(list, "");
    } catch (err) {
      console.error("Home data fetch error:", err);
      f7.dialog.alert(err.message || "Failed to fetch homepage financial health data", "API Error");
    } finally {
      loading = false;
    }
  }

  async function impersonateUser(userId, userName) {
    if (!userId || impersonatingId) return;
    impersonatingId = userId;
    try {
      const res = await orgMutation(
        "impersonate_user",
        { user_id: userId },
        "status token"
      );

      const token = res?.data?.impersonate_user?.token;
      if (token) {
        window.open(`https://app.trdr.money/external-login?token=${token}`, "_blank")?.focus();
      } else {
        f7.dialog.alert(`Could not generate impersonation token for ${userName || userId}`);
      }
    } catch (err) {
      console.error("Impersonation error:", err);
      f7.dialog.alert(err.message || "Impersonation failed", "Security Notice");
    } finally {
      impersonatingId = null;
    }
  }

  /**
   * Evaluates financial health anomaly heuristics
   */
  function evaluateHealth(user) {
    const portfolioVal = parseFloat(user.portfolio_value) || 0;
    const ledgerBal = parseFloat(user.ledger_balance) || 0;
    const invested = parseFloat(user.investment_amount) || 0;
    const payout = parseFloat(user.payout_amount) || 0;
    const pnl = parseFloat(user.profit_loss_amount) || 0;
    const perf = parseFloat(user.performance_percentage) || 0;

    const reasons = [];

    // Critical rules
    if (ledgerBal < ledgerThreshold) {
      reasons.push(`Negative Ledger (${formatINR(ledgerBal)})`);
    }
    if (portfolioVal < 0) {
      reasons.push(`Negative Portfolio (${formatINR(portfolioVal)})`);
    }

    if (reasons.length > 0) {
      return { status: "CRITICAL", reasons, isCritical: true, isWarning: false, isHealthy: false };
    }

    // Warning rules
    if (payout > invested + pnl && payout > 0) {
      reasons.push("Payouts exceed Capital + PnL");
    }
    if (perf < -80) {
      reasons.push(`Severe Drawdown (${perf.toFixed(1)}%)`);
    }

    if (reasons.length > 0) {
      return { status: "WARNING", reasons, isCritical: false, isWarning: true, isHealthy: false };
    }

    return { status: "HEALTHY", reasons: ["Nominal"], isCritical: false, isWarning: false, isHealthy: true };
  }

  // Pre-process accounts with health evaluations
  $: evaluatedUsers = rawUsers.map((u) => {
    const health = evaluateHealth(u);
    return {
      ...u,
      _health: health,
    };
  });

  // Calculate summary metrics
  $: totalAccounts = evaluatedUsers.length;
  $: criticalUsers = evaluatedUsers.filter((u) => u._health.isCritical);
  $: warningUsers = evaluatedUsers.filter((u) => u._health.isWarning);
  $: healthyUsers = evaluatedUsers.filter((u) => u._health.isHealthy);
  $: totalAnomalies = criticalUsers.length + warningUsers.length;

  $: totalAUM = evaluatedUsers.reduce((sum, u) => {
    const val = parseFloat(u.portfolio_value);
    return sum + (isNaN(val) ? 0 : val);
  }, 0);

  $: totalInvested = evaluatedUsers.reduce((sum, u) => {
    const val = parseFloat(u.investment_amount);
    return sum + (isNaN(val) ? 0 : val);
  }, 0);

  $: totalPayouts = evaluatedUsers.reduce((sum, u) => {
    const val = parseFloat(u.payout_amount);
    return sum + (isNaN(val) ? 0 : val);
  }, 0);

  // Filtered accounts based on user selection
  $: filteredUsers = evaluatedUsers.filter((u) => {
    // 1. Health category filter
    if (activeFilter === "ANOMALIES" && !u._health.isCritical && !u._health.isWarning) {
      return false;
    }
    if (activeFilter === "CRITICAL" && !u._health.isCritical) {
      return false;
    }
    if (activeFilter === "WARNING" && !u._health.isWarning) {
      return false;
    }
    if (activeFilter === "HEALTHY" && !u._health.isHealthy) {
      return false;
    }

    // 2. Search query filter
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      const matchName = String(u.user_name || "").toLowerCase().includes(q);
      const matchMobile = String(u.mobile || "").includes(q);
      const matchUcc = String(u.ucc || "").toLowerCase().includes(q);
      return matchName || matchMobile || matchUcc;
    }

    return true;
  });

  // Export columns for CSV
  const exportColumns = [
    { header: "UCC", key: "ucc" },
    { header: "Customer Name", key: "user_name" },
    { header: "Mobile", key: "mobile" },
    { header: "Health Status", format: (r) => r._health.status },
    { header: "Anomaly Reasons", format: (r) => r._health.reasons.join("; ") },
    { header: "Portfolio Value", key: "portfolio_value" },
    { header: "Ledger Balance", key: "ledger_balance" },
    { header: "Ledger Type", key: "ledger_balance_type" },
    { header: "Investment Amount", key: "investment_amount" },
    { header: "Payout Amount", key: "payout_amount" },
    { header: "Profit / Loss", key: "profit_loss_amount" },
    { header: "Performance %", key: "performance_percentage" },
    { header: "DP Total Value", key: "dp_total_value" },
    { header: "Long/Short Value", key: "longshort_value" },
  ];
</script>

<Page class="all-users-home-data-page">
  <AdminNavbar title="User Financial Health & AUM" subtitle="Task 5: Anomaly Heuristics, Ledger Audits & AUM Risk Engine" />

  <div class="page-content-wrapper padding">
    <!-- Top KPI Row -->
    <Row class="margin-bottom">
      <Col width="100" medium="20">
        <KpiStatCard
          title="Total AUM"
          value={formatINR(totalAUM)}
          subtitle="Aggregate Portfolio Value"
          icon="f7:chart_bar_alt_fill"
          iconColor="#2563eb"
          theme="info"
        />
      </Col>
      <Col width="100" medium="20">
        <KpiStatCard
          title="Audited Accounts"
          value={totalAccounts}
          subtitle="Live Active Clients"
          icon="f7:person_2_fill"
          iconColor="#475569"
          theme="default"
        />
      </Col>
      <Col width="100" medium="20">
        <KpiStatCard
          title="Capital Invested"
          value={formatINR(totalInvested)}
          subtitle="Total Payouts: {formatINR(totalPayouts)}"
          icon="f7:arrow_down_circle_fill"
          iconColor="#059669"
          theme="success"
        />
      </Col>
      <Col width="100" medium="20">
        <KpiStatCard
          title="Critical Anomalies"
          value={criticalUsers.length}
          subtitle="Negative Balances"
          icon="f7:exclamationmark_octagon_fill"
          iconColor="#ef4444"
          theme="danger"
          clickable={true}
          active={activeFilter === "CRITICAL"}
          on:click={() => (activeFilter = activeFilter === "CRITICAL" ? "ALL" : "CRITICAL")}
        />
      </Col>
      <Col width="100" medium="20">
        <KpiStatCard
          title="Warning Anomalies"
          value={warningUsers.length}
          subtitle="Payout / Drawdown Risks"
          icon="f7:exclamationmark_triangle_fill"
          iconColor="#f59e0b"
          theme="warning"
          clickable={true}
          active={activeFilter === "WARNING"}
          on:click={() => (activeFilter = activeFilter === "WARNING" ? "ALL" : "WARNING")}
        />
      </Col>
    </Row>

    <!-- Controls & Action Toolbar -->
    <div class="controls-toolbar margin-bottom">
      <!-- Quick Filter Pills -->
      <div class="filter-pills">
        <button
          class="pill-btn"
          class:active={activeFilter === "ALL"}
          on:click={() => (activeFilter = "ALL")}
        >
          All ({totalAccounts})
        </button>
        <button
          class="pill-btn danger"
          class:active={activeFilter === "ANOMALIES"}
          on:click={() => (activeFilter = "ANOMALIES")}
        >
          ⚠️ Anomalies ({totalAnomalies})
        </button>
        <button
          class="pill-btn red"
          class:active={activeFilter === "CRITICAL"}
          on:click={() => (activeFilter = "CRITICAL")}
        >
          Critical ({criticalUsers.length})
        </button>
        <button
          class="pill-btn amber"
          class:active={activeFilter === "WARNING"}
          on:click={() => (activeFilter = "WARNING")}
        >
          Warnings ({warningUsers.length})
        </button>
        <button
          class="pill-btn green"
          class:active={activeFilter === "HEALTHY"}
          on:click={() => (activeFilter = "HEALTHY")}
        >
          Healthy ({healthyUsers.length})
        </button>
      </div>

      <!-- Search Box -->
      <div class="search-input-wrap">
        <input
          type="text"
          placeholder="Filter by name, mobile, UCC..."
          bind:value={searchQuery}
          class="search-input"
        />
        {#if searchQuery}
          <button class="clear-search" on:click={() => (searchQuery = "")}>✕</button>
        {/if}
      </div>

      <!-- Export CSV -->
      <div class="export-wrap">
        <CsvExportButton
          filename="financial_health_audit_{new Date().toISOString().slice(0, 10)}.csv"
          columns={exportColumns}
          data={filteredUsers}
          label="Export Health Audit CSV"
        />
      </div>

      <!-- Refresh -->
      <Button small outline color="blue" on:click={fetchAllHomeData} disabled={loading}>
        {#if loading}
          <Preloader size={14} />
        {:else}
          <span>🔄 Refresh</span>
        {/if}
      </Button>
    </div>

    <!-- Data Table Card -->
    <Card class="table-card">
      <CardContent class="no-padding">
        {#if loading}
          <div class="loading-state">
            <Preloader size={32} />
            <p>Evaluating account heuristics and portfolio values...</p>
          </div>
        {:else if filteredUsers.length === 0}
          <div class="empty-state">
            <span class="empty-icon">🛡️</span>
            <p>No accounts match the selected filter criteria.</p>
          </div>
        {:else}
          <div class="data-table-responsive">
            <table class="health-table">
              <thead>
                <tr>
                  <th style="width: 40px;">#</th>
                  <th>Health</th>
                  <th>Customer / UCC</th>
                  <th>Mobile</th>
                  <th>Portfolio Value</th>
                  <th>Ledger Balance</th>
                  <th>Profit / Loss</th>
                  <th>Perf %</th>
                  <th>Invested</th>
                  <th>Payouts</th>
                  <th>Anomaly Diagnostics</th>
                  <th style="width: 70px;">Desk</th>
                </tr>
              </thead>
              <tbody>
                {#each filteredUsers as user, i}
                  <tr
                    class:row-critical={user._health.isCritical}
                    class:row-warning={user._health.isWarning}
                  >
                    <td class="col-index">{i + 1}</td>
                    <td class="col-health">
                      <StatusBadge status={user._health.status} size="small" />
                    </td>
                    <td class="col-name">
                      <div class="user-block">
                        <span class="user-name">{user.user_name || "Unknown"}</span>
                        <a
                          href="/all-reports-of-user/?ucc={user.ucc}"
                          class="user-ucc-link"
                          title="View Reports for UCC {user.ucc}"
                        >
                          UCC: {user.ucc || "-"} ↗
                        </a>
                      </div>
                    </td>
                    <td class="col-mobile">{user.mobile || "-"}</td>
                    <td class="col-amount">
                      <span class="val-bold">{formatINR(user.portfolio_value)}</span>
                    </td>
                    <td class="col-amount">
                      <span
                        class="val-ledger"
                        class:is-negative={parseFloat(user.ledger_balance) < 0}
                      >
                        {formatINR(user.ledger_balance)}
                      </span>
                    </td>
                    <td class="col-amount">
                      <span
                        class="val-pnl"
                        class:pnl-positive={parseFloat(user.profit_loss_amount) > 0}
                        class:pnl-negative={parseFloat(user.profit_loss_amount) < 0}
                      >
                        {formatINR(user.profit_loss_amount)}
                      </span>
                    </td>
                    <td class="col-perf">
                      <span class="perf-text">{formatPercent(user.performance_percentage)}</span>
                    </td>
                    <td class="col-amount">{formatINR(user.investment_amount)}</td>
                    <td class="col-amount">{formatINR(user.payout_amount)}</td>
                    <td class="col-diagnostics">
                      {#if user._health.reasons && user._health.reasons[0] !== "Nominal"}
                        <div class="diag-pills">
                          {#each user._health.reasons as r}
                            <span
                              class="diag-pill"
                              class:pill-crit={user._health.isCritical}
                              class:pill-warn={user._health.isWarning}
                            >
                              {r}
                            </span>
                          {/each}
                        </div>
                      {:else}
                        <span class="text-healthy">✓ Nominal</span>
                      {/if}
                    </td>
                    <td class="col-actions">
                      <button
                        class="impersonate-btn"
                        title="Impersonate Member Dashboard"
                        disabled={impersonatingId === user.user_id}
                        on:click={() => impersonateUser(user.user_id, user.user_name)}
                      >
                        {#if impersonatingId === user.user_id}
                          <Preloader size={14} />
                        {:else}
                          <span>👀</span>
                        {/if}
                      </button>
                    </td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        {/if}
      </CardContent>
    </Card>
  </div>
</Page>

<style>
  .page-content-wrapper {
    max-width: 1440px;
    margin: 0 auto;
  }

  .controls-toolbar {
    display: flex;
    align-items: center;
    gap: 12px;
    background: #ffffff;
    padding: 12px 16px;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    flex-wrap: wrap;
  }

  .filter-pills {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }

  .pill-btn {
    border: 1px solid #cbd5e1;
    background: #f8fafc;
    color: #475569;
    font-size: 12px;
    font-weight: 600;
    padding: 6px 12px;
    border-radius: 20px;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .pill-btn:hover {
    background: #f1f5f9;
  }

  .pill-btn.active {
    background: #0f172a;
    color: #ffffff;
    border-color: #0f172a;
  }

  .pill-btn.danger.active {
    background: #ef4444;
    border-color: #ef4444;
    color: #ffffff;
  }

  .pill-btn.red.active {
    background: #dc2626;
    border-color: #dc2626;
    color: #ffffff;
  }

  .pill-btn.amber.active {
    background: #d97706;
    border-color: #d97706;
    color: #ffffff;
  }

  .pill-btn.green.active {
    background: #16a34a;
    border-color: #16a34a;
    color: #ffffff;
  }

  .search-input-wrap {
    flex: 1;
    min-width: 200px;
    position: relative;
    display: flex;
    align-items: center;
  }

  .search-input {
    width: 100%;
    height: 34px;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    padding: 0 28px 0 12px;
    font-size: 13px;
    color: #0f172a;
    background: #f8fafc;
  }

  .search-input:focus {
    background: #ffffff;
    border-color: #3b82f6;
    outline: none;
  }

  .clear-search {
    position: absolute;
    right: 8px;
    background: none;
    border: none;
    font-size: 12px;
    color: #94a3b8;
    cursor: pointer;
  }

  .table-card {
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    overflow: hidden;
  }

  .data-table-responsive {
    overflow-x: auto;
    width: 100%;
  }

  .health-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    text-align: left;
  }

  .health-table thead th {
    background: #f8fafc;
    color: #475569;
    font-weight: 600;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    padding: 12px 12px;
    border-bottom: 1px solid #e2e8f0;
    white-space: nowrap;
  }

  .health-table tbody td {
    padding: 12px 12px;
    border-bottom: 1px solid #f1f5f9;
    color: #1e293b;
    vertical-align: middle;
  }

  .health-table tbody tr:hover td {
    background: #f8fafc;
  }

  .health-table tbody tr.row-critical td {
    background: #fff5f5;
  }

  .health-table tbody tr.row-warning td {
    background: #fffbeb;
  }

  .col-index {
    color: #94a3b8;
    font-size: 12px;
  }

  .user-block {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .user-name {
    font-weight: 600;
    color: #0f172a;
  }

  .user-ucc-link {
    font-family: monospace;
    font-size: 11px;
    color: #2563eb;
    text-decoration: none;
    font-weight: 600;
  }

  .user-ucc-link:hover {
    text-decoration: underline;
  }

  .col-mobile {
    font-family: monospace;
    font-size: 12px;
    color: #475569;
  }

  .val-bold {
    font-weight: 700;
    color: #0f172a;
  }

  .val-ledger {
    font-weight: 600;
  }

  .val-ledger.is-negative {
    color: #dc2626;
    background: #fee2e2;
    padding: 2px 6px;
    border-radius: 4px;
    font-weight: 700;
  }

  .val-pnl {
    font-weight: 600;
  }

  .val-pnl.pnl-positive {
    color: #15803d;
  }

  .val-pnl.pnl-negative {
    color: #b91c1c;
  }

  .perf-text {
    font-size: 12px;
    font-weight: 600;
    color: #475569;
  }

  .diag-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }

  .diag-pill {
    font-size: 11px;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 4px;
    white-space: nowrap;
  }

  .diag-pill.pill-crit {
    background: #fee2e2;
    color: #b91c1c;
    border: 1px solid #fca5a5;
  }

  .diag-pill.pill-warn {
    background: #fef3c7;
    color: #b45309;
    border: 1px solid #fde68a;
  }

  .text-healthy {
    color: #16a34a;
    font-size: 12px;
    font-weight: 600;
  }

  .impersonate-btn {
    background: #f1f5f9;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    padding: 4px 8px;
    cursor: pointer;
    font-size: 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s ease;
  }

  .impersonate-btn:hover {
    background: #e2e8f0;
  }

  .loading-state,
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 50px 20px;
    color: #64748b;
  }

  .empty-icon {
    font-size: 36px;
    margin-bottom: 8px;
  }
</style>
